import type { Auth, User } from 'firebase/auth';

import { getFirebaseApp } from '@/services/firebase/config';

let authInstance: Auth | null = null;

export async function getAuth(): Promise<Auth> {
  if (authInstance) return authInstance;
  const app = await getFirebaseApp();
  const {
    getAuth: getFirebaseAuth,
    browserLocalPersistence,
    setPersistence,
  } = await import('firebase/auth');
  authInstance = getFirebaseAuth(app);
  await setPersistence(authInstance, browserLocalPersistence);
  return authInstance;
}

export async function registerWithEmail(email: string, password: string): Promise<User> {
  const auth = await getAuth();
  const { createUserWithEmailAndPassword } = await import('firebase/auth');
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  const auth = await getAuth();
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export async function signInWithGoogle(): Promise<User> {
  const auth = await getAuth();
  const { GoogleAuthProvider, signInWithPopup } = await import('firebase/auth');
  const provider = new GoogleAuthProvider();
  const credential = await signInWithPopup(auth, provider);
  return credential.user;
}

export async function sendPasswordReset(email: string): Promise<void> {
  const auth = await getAuth();
  const { sendPasswordResetEmail } = await import('firebase/auth');
  await sendPasswordResetEmail(auth, email);
}

export async function signOut(): Promise<void> {
  const auth = await getAuth();
  const { signOut: firebaseSignOut } = await import('firebase/auth');
  await firebaseSignOut(auth);
}

export async function deleteCurrentUser(): Promise<void> {
  const auth = await getAuth();
  const user = auth.currentUser;
  if (!user) return;
  const { deleteUser } = await import('firebase/auth');
  await deleteUser(user);
}

export async function watchAuthState(callback: (user: User | null) => void): Promise<() => void> {
  const auth = await getAuth();
  const { onAuthStateChanged } = await import('firebase/auth');
  return onAuthStateChanged(auth, callback);
}

export function mapFirebaseAuthError(error: unknown): string {
  if (typeof error !== 'object' || error === null) return 'Wystąpił nieznany błąd';
  const code = (error as { code?: string }).code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return 'Nieprawidłowy adres email';
    case 'auth/email-already-in-use':
      return 'Ten email jest już zarejestrowany';
    case 'auth/weak-password':
      return 'Hasło jest zbyt słabe (min. 6 znaków)';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Nieprawidłowy email lub hasło';
    case 'auth/network-request-failed':
      return 'Brak połączenia z internetem';
    case 'auth/popup-closed-by-user':
      return 'Logowanie anulowane';
    case 'auth/too-many-requests':
      return 'Za dużo prób. Spróbuj ponownie później';
    default:
      return (error as { message?: string }).message ?? 'Wystąpił nieznany błąd';
  }
}
